import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { observer } from 'mobx-react';
import { compose } from 'recompose';

import useToast from '../../../../hooks/useToast';
import Promotion from '../../../../Model/Promotion';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import { AddButton } from '../../../../../components/Button/ActionButtons';
import PromotionModal from './PromotionModal';
import PromotionsTable from './PromotionsTable';

import {
  PromotionContainer,
  EmptyState,
  ButtonContainer,
} from './styles';
import texts from './texts.json';

const Promotions = ({ coachId }) => {
  const [showModal, setShowModal] = useState(false);
  const [promotionCollection, setPromotionCollection] = useState({ docs: [] });
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const fetchPromotions = async () => {
      if (!coachId) return;

      setIsLoading(true);
      try {
        const collection = await Promotion.getCurrentPromotionsByCoachId(coachId);
        if (isComponentMountedRef.current) {
          setPromotionCollection(collection);
        }
      } catch (error) {
        showToast(format(texts.errorFetchingPromotions, { error: error.message }), { error: true });
      } finally {
        if (isComponentMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchPromotions();
  }, [
    coachId,
    isComponentMountedRef,
    showToast,
  ]);

  const handleEditPromotion = useCallback((promotionId) => {
    const promotion = promotionCollection?.docs.find((doc) => doc.id === promotionId);
    setSelectedPromotion(promotion);
    setShowModal(true);
  }, [promotionCollection.docs]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedPromotion(null);
  }, []);

  return (
    <>
      <PromotionContainer>
        <ButtonContainer>
          <AddButton
            variant="normal"
            onClick={() => setShowModal(true)}
          >
            {texts.addPromotion}
          </AddButton>
        </ButtonContainer>
        {promotionCollection.docs.length > 0 ? (
          <PromotionsTable
            promotionDocs={promotionCollection.docs.slice()}
            onEdit={handleEditPromotion}
          />
        ) : (
          <EmptyState>
            {texts.noPromotions}
          </EmptyState>
        )}
        {showModal && (
        <PromotionModal
          coachId={coachId}
          onClose={handleCloseModal}
          selectedPromotion={selectedPromotion}
        />
        )}
      </PromotionContainer>
      <LoadingOverlay isLoading={isLoading} />
    </>
  );
};

Promotions.propTypes = {
  coachId: PropTypes.string.isRequired,
};

export default compose(
  observer,
)(Promotions);
